import React from "react";
import { CheckCircle2, Circle, XCircle } from "lucide-react";
import { motion } from "framer-motion";

function StepIcon({ state }) {
  if (state === "completed") {
    return (
      <CheckCircle2
        size={20}
        className="text-[rgb(var(--color-success))]"
        aria-hidden="true"
      />
    );
  }

  if (state === "blocked") {
    return (
      <XCircle
        size={20}
        className="text-[rgb(var(--color-danger))]"
        aria-hidden="true"
      />
    );
  }

  if (state === "current") {
    return (
      <motion.span
        animate={{ scale: [1, 1.15, 1] }}
        transition={{ duration: 1.2, repeat: Infinity }}
        className="flex h-5 w-5 items-center justify-center rounded-full border-2 border-[rgb(var(--color-primary))]"
      >
        <span className="h-2 w-2 rounded-full bg-[rgb(var(--color-primary))]" />
      </motion.span>
    );
  }

  return (
    <Circle
      size={20}
      className="text-[rgb(var(--color-text-muted))]"
      aria-hidden="true"
    />
  );
}

function getStepState(stepKey, currentStep, completedSteps, blockedSteps) {
  if (blockedSteps.includes(stepKey)) return "blocked";
  if (completedSteps.includes(stepKey)) return "completed";
  if (stepKey === currentStep) return "current";
  return "pending";
}

export default function StateStepper({
  steps,
  currentStep,
  mode = "horizontal",
  completedSteps = [],
  blockedSteps = [],
}) {
  const vertical = mode === "vertical";

  return (
    <ol
      className={
        vertical
          ? "flex flex-col gap-4"
          : "flex w-full items-start justify-between gap-4"
      }
    >
      {steps.map((step, index) => {
        const state = getStepState(
          step.key,
          currentStep,
          completedSteps,
          blockedSteps
        );

        const labelClass =
          state === "completed"
            ? "text-[rgb(var(--color-success))]"
            : state === "blocked"
              ? "text-[rgb(var(--color-danger))]"
              : state === "current"
                ? "font-semibold text-[rgb(var(--color-primary))]"
                : "text-[rgb(var(--color-text-muted))]";

        return (
          <li
            key={step.key}
            className={
              vertical
                ? "relative flex gap-3"
                : "relative flex flex-1 flex-col items-center gap-2"
            }
          >
            {index < steps.length - 1 && (
              <span
                className={
                  vertical
                    ? "absolute left-2.5 top-6 h-full w-px bg-[rgb(var(--color-border))]"
                    : "absolute left-1/2 top-2.5 h-px w-full bg-[rgb(var(--color-border))]"
                }
                aria-hidden="true"
              />
            )}

            <div className="relative z-10 bg-[rgb(var(--color-surface))]">
              <StepIcon state={state} />
            </div>

            <div
              className={`text-tok-sm ${labelClass} ${
                vertical ? "" : "text-center"
              }`}
            >
              {step.label}
            </div>
          </li>
        );
      })}
    </ol>
  );
}
